/**
 * Proforma detail (ADR-0032) — invoice-detail peer for non-fiscal quotes.
 *
 * @see prototype/sales/invoice-detail.html — document-first detail layout
 */
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileCheck2,
  FileText,
  Pencil,
  Trash2,
  XCircle,
} from "lucide-react";
import { Alert, Button, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { deriveInitials, formatDate, paiseToInrExact } from "../../../lib/format.ts";
import { proformaStatusLabel } from "../lib/proforma-status.ts";

type ProformaStatus = "draft" | "finalized" | "converted" | "expired";

export function ProformaDetailPage({ proformaId }: { proformaId: string }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const query = trpc.proformas.get.useQuery({ proformaId });
  const company = trpc.companies.get.useQuery({});

  const invalidate = async () => {
    await Promise.all([
      utils.proformas.get.invalidate({ proformaId }),
      utils.proformas.list.invalidate(),
    ]);
  };

  const finalize = trpc.proformas.finalize.useMutation({
    onSuccess: async (r) => {
      await invalidate();
      toast.success(`Proforma ${r.proforma.docNumber} finalized.`);
    },
    onError: (e) => toast.error(e.message),
  });

  const convert = trpc.proformas.convert.useMutation({
    onSuccess: async (r) => {
      await invalidate();
      if (r.outcome === "issued" && r.invoiceId) {
        toast.success(`Tax invoice ${r.invoiceDocNumber} created.`);
        void navigate({ to: "/sales/invoices/$invoiceId", params: { invoiceId: r.invoiceId } });
      } else {
        toast.info("Conversion needs discount approval — complete it from the invoice drafts.");
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const expire = trpc.proformas.expire.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Proforma marked expired.");
    },
    onError: (e) => toast.error(e.message),
  });

  const del = trpc.proformas.delete.useMutation({
    onSuccess: async () => {
      await utils.proformas.list.invalidate();
      toast.success("Draft proforma deleted.");
      void navigate({ to: "/sales/proformas" });
    },
    onError: (e) => toast.error(e.message),
  });

  if (query.isLoading || company.isLoading) {
    return (
      <div className="invoice-detail-page">
        <p className="p-6 text-sm text-muted">Loading proforma...</p>
      </div>
    );
  }

  if (query.error || !query.data) {
    return (
      <div className="invoice-detail-page p-6">
        <Alert
          intent="bad"
          title="Couldn't load proforma"
          description={query.error?.message ?? "Proforma not found."}
        />
      </div>
    );
  }

  if (company.error || !company.data) {
    return (
      <div className="invoice-detail-page p-6">
        <Alert
          intent="bad"
          title="Couldn't load company profile"
          description={
            company.error?.message ??
            "Settings -> Company profile is required for proforma details."
          }
        />
      </div>
    );
  }

  const { proforma: p, lines, customer } = query.data;
  const busy = finalize.isPending || convert.isPending || expire.isPending || del.isPending;
  const docNumber = p.docNumber || "Draft proforma";
  const isForeign =
    p.currencyCode !== "INR" && p.foreignAmountMinor !== null && p.fxRateToInrPaise !== null;
  const foreignAmountMinor = isForeign ? p.foreignAmountMinor! : null;
  const fxRateToInrPaise = isForeign ? p.fxRateToInrPaise! : null;
  const totalLabel = isForeign
    ? formatForeign(p.currencyCode, foreignAmountMinor!)
    : paiseToInrExact(p.totalPaise);

  return (
    <div className="invoice-detail-page">
      <header className="composer-head">
        <Link to="/sales/proformas" className="composer-head-back" aria-label="Back to proformas">
          <ArrowLeft size={16} strokeWidth={2.2} />
        </Link>
        <div className="composer-head-text">
          <h1>
            <span>Proforma</span>
            <span className="doc-num">{docNumber}</span>
            <span className={`status-pill ${statusVariant(p.status)}`}>
              {proformaStatusLabel(p.status)}
            </span>
          </h1>
          <p className="sub">
            <strong>{customer.name}</strong>
            <span className="sep" aria-hidden>
              ·
            </span>
            <span>
              Dated <span className="num">{formatDate(p.issuedOn)}</span>
            </span>
            {p.expiresOn ? (
              <>
                <span className="sep" aria-hidden>
                  ·
                </span>
                <span>
                  Valid until <span className="num">{formatDate(p.expiresOn)}</span>
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="composer-head-actions">
          {p.status === "draft" ? (
            <>
              <Button asChild intent="ghost" size="sm">
                <Link to="/sales/proformas/$proformaId/edit" params={{ proformaId: p.id }}>
                  <Pencil size={15} />
                  <span>Edit</span>
                </Link>
              </Button>
              <Button
                intent="danger"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (confirm("Delete this draft proforma?")) del.mutate({ proformaId: p.id });
                }}
              >
                <Trash2 size={15} />
                <span>Delete</span>
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => finalize.mutate({ proformaId: p.id })}
              >
                <CheckCircle2 size={15} />
                <span>Finalize</span>
              </Button>
            </>
          ) : null}
          {p.status === "finalized" ? (
            <>
              <Button
                intent="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (confirm("Mark this proforma as expired?"))
                    expire.mutate({ proformaId: p.id });
                }}
              >
                <Clock size={15} />
                <span>Expire</span>
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (
                    confirm(
                      "Convert to a real tax invoice? This assigns an invoice number and posts to the ledger.",
                    )
                  ) {
                    convert.mutate({ proformaId: p.id });
                  }
                }}
              >
                <FileCheck2 size={15} />
                <span>Convert to invoice</span>
              </Button>
            </>
          ) : null}
          {p.status === "converted" && p.convertedToInvoiceId ? (
            <Button asChild size="sm">
              <Link to="/sales/invoices/$invoiceId" params={{ invoiceId: p.convertedToInvoiceId }}>
                <FileText size={15} />
                <span>View tax invoice</span>
              </Link>
            </Button>
          ) : null}
        </div>
      </header>

      <div className="doc-stage">
        <div className="doc-stage-main">
          <section className="ribbon amber" aria-label="Proforma disclaimer">
            <span className="ic" aria-hidden>
              <FileText size={15} />
            </span>
            <div className="body">
              <strong>Proforma invoice</strong> — this is not a valid tax invoice. GST is not
              payable on this document; it is a quote pending confirmation.
            </div>
          </section>

          <article className="doc" aria-label="Printable proforma document">
            <div className="doc-bar">
              <span className="filename">
                <strong>{docNumber}.pdf</strong> · preview document
              </span>
            </div>
            <div className="doc-paper">
              <header className="doc-letterhead">
                <div className="seller">
                  <div className="logo" aria-hidden>
                    {deriveInitials(company.data.legalName)}
                  </div>
                  <h2>{company.data.legalName}</h2>
                  <div className="addr">
                    {companyAddress(company.data) ? (
                      <strong>{companyAddress(company.data)}</strong>
                    ) : null}
                    {company.data.gstin ? (
                      <>
                        <br />
                        GSTIN&nbsp;<span className="num">{company.data.gstin}</span>
                      </>
                    ) : (
                      <>
                        <br />
                        GSTIN not on file
                      </>
                    )}
                  </div>
                </div>
                <div className="marker">
                  <div className="label">Proforma invoice</div>
                  <h1>{docNumber}</h1>
                  <div className="num-line">
                    <span className="k">Dated</span>
                    {formatDate(p.issuedOn)}
                  </div>
                  <div className="num-line">
                    <span className="k">Status</span>
                    {proformaStatusLabel(p.status)}
                  </div>
                </div>
              </header>

              <div className="doc-meta-grid">
                <div className="to">
                  <div className="lbl">Bill to</div>
                  <div className="name">{customer.name}</div>
                  <div className="addr">
                    {customer.addressLine1 ? <div>{customer.addressLine1}</div> : null}
                    {customer.addressLine2 ? <div>{customer.addressLine2}</div> : null}
                    {customer.city || customer.country || customer.state ? (
                      <div>
                        {[customer.city, customer.country ?? customer.state]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    ) : (
                      <div>Address not on file</div>
                    )}
                    {customer.phone ? <div>Ph {customer.phone}</div> : null}
                    {customer.gstin ? (
                      <span className="num">GSTIN {customer.gstin}</span>
                    ) : (
                      <span className="num">Unregistered counterparty</span>
                    )}
                  </div>
                </div>
                <div className="dates">
                  <div className="cell">
                    <div className="k">Dated</div>
                    <div className="v">{formatDate(p.issuedOn)}</div>
                  </div>
                  <div className="cell">
                    <div className="k">Valid until</div>
                    <div className="v">{p.expiresOn ? formatDate(p.expiresOn) : "-"}</div>
                  </div>
                  <div className="cell">
                    <div className="k">Place of supply</div>
                    <div className="v">{p.placeOfSupply}</div>
                    <div className="sub">{taxTreatmentLabel(p.taxTreatment)}</div>
                  </div>
                  <div className="cell">
                    <div className="k">PO reference</div>
                    <div className="v">{p.poReference ?? "-"}</div>
                  </div>
                </div>
              </div>

              <table className="doc-items" aria-label="Line items">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="r">HSN</th>
                    <th className="r">Qty</th>
                    <th className="r">Rate{isForeign ? ` (${p.currencyCode})` : ""}</th>
                    <th className="r">Tax</th>
                    <th className="r">Amount{isForeign ? ` (${p.currencyCode})` : ""}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.id}>
                      <td className="desc">
                        <strong>{line.description}</strong>
                        <span className="sub">{line.unit}</span>
                      </td>
                      <td className="r">{line.hsn}</td>
                      <td className="r">
                        {formatQty(line.qtyMilli)} {line.unit}
                      </td>
                      <td className="r">
                        {line.rateForeignMinor && isForeign
                          ? minorToMajor(line.rateForeignMinor)
                          : withoutRupee(paiseToInrExact(line.ratePaise))}
                      </td>
                      <td className="r">{line.taxRateBps / 100}%</td>
                      <td className="r amount">
                        {line.lineTotalForeignMinor && isForeign
                          ? minorToMajor(line.lineTotalForeignMinor)
                          : withoutRupee(paiseToInrExact(line.lineTotalPaise))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="doc-totals">
                <div className="note">
                  <strong>Non-fiscal quote.</strong> This document records the quoted amount only.
                  Conversion creates the real tax invoice and ledger posting.
                </div>
                <div className="stack">
                  {isForeign ? (
                    <>
                      <div className="row">
                        <span>Quote total ({p.currencyCode})</span>
                        <span className="v">
                          {formatForeign(p.currencyCode, foreignAmountMinor!)}
                        </span>
                      </div>
                      <div className="row">
                        <span>FX rate</span>
                        <span className="v">
                          ₹{minorToMajor(fxRateToInrPaise!)}/{p.currencyCode}
                        </span>
                      </div>
                      <div className="row">
                        <span>INR quote value</span>
                        <span className="v">{paiseToInrExact(p.totalPaise)}</span>
                      </div>
                      <div className="row grand">
                        <span>Customer quote</span>
                        <span className="v">
                          {formatForeign(p.currencyCode, foreignAmountMinor!)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="row">
                        <span>Subtotal · taxable value</span>
                        <span className="v">{withoutRupee(paiseToInrExact(p.subtotalPaise))}</span>
                      </div>
                      {p.cgstPaise !== "0" ? (
                        <div className="row">
                          <span>CGST</span>
                          <span className="v">{withoutRupee(paiseToInrExact(p.cgstPaise))}</span>
                        </div>
                      ) : null}
                      {p.sgstPaise !== "0" ? (
                        <div className="row">
                          <span>SGST</span>
                          <span className="v">{withoutRupee(paiseToInrExact(p.sgstPaise))}</span>
                        </div>
                      ) : null}
                      {p.igstPaise !== "0" ? (
                        <div className="row">
                          <span>IGST</span>
                          <span className="v">{withoutRupee(paiseToInrExact(p.igstPaise))}</span>
                        </div>
                      ) : null}
                      <div className="row grand">
                        <span>Quote total</span>
                        <span className="v">{paiseToInrExact(p.totalPaise)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="doc-foot">
                <div>
                  <strong style={{ color: "var(--ink-2)" }}>Notes to customer.</strong>{" "}
                  {p.customerNote ??
                    "This quote is subject to confirmation. A tax invoice will be issued after conversion."}
                </div>
                <div className="pay">
                  <span className="k">Quoted by</span> <strong>{company.data.legalName}</strong>
                  <br />
                  <span className="k">Bank</span> {company.data.bankName ?? "-"}
                  <br />
                  <span className="k">A/c</span> {company.data.bankAccountNumber ?? "-"}
                  <br />
                  <span className="k">IFSC</span> {company.data.bankIfsc ?? "-"}
                  {company.data.bankUpiId ? (
                    <>
                      <br />
                      <span className="k">UPI</span> <strong>{company.data.bankUpiId}</strong>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </article>
        </div>

        <aside className="rail" aria-label="Proforma summary">
          <section className="rail-card rail-next">
            <div className="head">
              <h4>Next action</h4>
            </div>
            <div className="body">
              <p className="pri-line">
                <strong>{nextActionTitle(p.status)}</strong> {nextActionCopy(p.status)}
              </p>
              <div className="actions">
                {p.status === "draft" ? (
                  <>
                    <Link
                      to="/sales/proformas/$proformaId/edit"
                      params={{ proformaId: p.id }}
                      className="btn secondary"
                    >
                      Edit draft
                    </Link>
                    <button
                      type="button"
                      className="btn primary"
                      disabled={busy}
                      onClick={() => finalize.mutate({ proformaId: p.id })}
                    >
                      Finalize quote
                    </button>
                  </>
                ) : null}
                {p.status === "finalized" ? (
                  <button
                    type="button"
                    className="btn primary"
                    disabled={busy}
                    onClick={() => {
                      if (
                        confirm(
                          "Convert to a real tax invoice? This assigns an invoice number and posts to the ledger.",
                        )
                      ) {
                        convert.mutate({ proformaId: p.id });
                      }
                    }}
                  >
                    Convert to tax invoice
                  </button>
                ) : null}
                {p.status === "converted" && p.convertedToInvoiceId ? (
                  <Link
                    to="/sales/invoices/$invoiceId"
                    params={{ invoiceId: p.convertedToInvoiceId }}
                    className="btn primary"
                  >
                    View tax invoice
                  </Link>
                ) : null}
              </div>
            </div>
          </section>

          <section className="rail-card rail-money">
            <div className="head">
              <h4>Quote total</h4>
            </div>
            <div className="body">
              {isForeign ? (
                <>
                  <RailRow label="Customer quote" value={totalLabel} />
                  <RailRow label="FX rate" value={`₹${minorToMajor(fxRateToInrPaise!)}`} />
                  <RailRow label="INR book value" value={paiseToInrExact(p.totalPaise)} />
                </>
              ) : (
                <>
                  <RailRow label="Taxable" value={paiseToInrExact(p.subtotalPaise)} />
                  {p.cgstPaise !== "0" ? (
                    <RailRow label="CGST" value={paiseToInrExact(p.cgstPaise)} />
                  ) : null}
                  {p.sgstPaise !== "0" ? (
                    <RailRow label="SGST" value={paiseToInrExact(p.sgstPaise)} />
                  ) : null}
                  {p.igstPaise !== "0" ? (
                    <RailRow label="IGST" value={paiseToInrExact(p.igstPaise)} />
                  ) : null}
                </>
              )}
              <div className="row grand">
                <span>Total</span>
                <span className="v">{totalLabel}</span>
              </div>
            </div>
          </section>

          <section className="rail-card rail-party">
            <div className="head">
              <h4>Customer</h4>
            </div>
            <div className="body">
              <div className="who">
                <div className="ava" aria-hidden>
                  {deriveInitials(customer.name)}
                </div>
                <div className="copy">
                  <Link
                    to="/sales/customers/$customerId"
                    params={{ customerId: customer.id }}
                    className="name"
                  >
                    {customer.name}
                  </Link>
                  <div className="meta">{customer.gstin ?? "Unregistered"}</div>
                </div>
              </div>
              <RailField label="Phone" value={customer.phone ?? "-"} />
              <RailField label="Email" value={customer.email ?? "-"} />
              <RailField label="City" value={customer.city ?? "-"} />
            </div>
          </section>

          {p.status === "expired" ? (
            <section className="rail-card">
              <div className="body flex items-start gap-3 text-sm text-bad">
                <XCircle size={18} className="mt-0.5 shrink-0" />
                <span>This proforma is expired and cannot be converted.</span>
              </div>
            </section>
          ) : null}
        </aside>
      </div>

      <footer className="composer-foot composer-foot--summary">
        <div className="composer-foot-context">
          <span className="status-tag">{proformaStatusLabel(p.status)}</span>
          <strong>{customer.name}</strong>
          <span className="sep" aria-hidden>
            ·
          </span>
          <span className="num">{docNumber}</span>
        </div>
        <div className="composer-foot-amount">
          <span className="caption">quote total</span>
          <span className="amount">{totalLabel}</span>
        </div>
      </footer>
    </div>
  );
}

function RailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row">
      <span>{label}</span>
      <span className="v">{value}</span>
    </div>
  );
}

function RailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      <span className="k">{label}</span>
      <span className="v">{value}</span>
    </div>
  );
}

function companyAddress(company: {
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
}): string {
  return [company.address, company.city, company.state, company.pincode].filter(Boolean).join(", ");
}

function minorToMajor(value: string): string {
  const minor = BigInt(value || "0");
  const sign = minor < 0n ? "-" : "";
  const abs = minor < 0n ? -minor : minor;
  return `${sign}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}

function formatForeign(currencyCode: string, minor: string): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
  }).format(Number(minor) / 100);
}

function withoutRupee(value: string): string {
  return value.replace("₹", "").trim();
}

function formatQty(qtyMilli: number): string {
  if (qtyMilli % 1000 === 0) return String(qtyMilli / 1000);
  return (qtyMilli / 1000).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

function statusVariant(status: ProformaStatus): string {
  if (status === "draft") return "draft";
  if (status === "finalized") return "issued";
  if (status === "converted") return "paid";
  return "void";
}

function taxTreatmentLabel(treatment: string): string {
  if (treatment === "export_lut") return "Export · zero-rated (LUT)";
  if (treatment === "export_with_tax") return "Export · tax charged";
  if (treatment === "sez_lut") return "SEZ · zero-rated (LUT)";
  if (treatment === "sez_with_tax") return "SEZ · tax charged";
  if (treatment === "deemed_export") return "Deemed export";
  return "Domestic supply";
}

function nextActionTitle(status: ProformaStatus): string {
  if (status === "draft") return "Finish the quote.";
  if (status === "finalized") return "Ready to convert.";
  if (status === "converted") return "Tax invoice exists.";
  return "Expired quote.";
}

function nextActionCopy(status: ProformaStatus): string {
  if (status === "draft") return "Finalize it before sending or converting.";
  if (status === "finalized") return "Conversion creates the fiscal invoice and ledger entry.";
  if (status === "converted") return "Open the issued invoice for receipts and follow-up.";
  return "Create a new proforma if the customer wants a fresh quote.";
}
